/**
 * Server Wake-up Utility for Staging Environment
 * Handles server sleep/wake cycle on staging deployments
 */

import { BACKEND_URL } from "./apiBase";
const isStaging = process.env.NODE_ENV === 'development' || 
                  process.env.REACT_APP_ENV === 'staging';

/**
 * Check if server is awake by pinging /api/health endpoint
 */
const checkServerHealth = async () => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/health`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    return response.ok;
  } catch (error) {
    return false;
  }
};

/**
 * Poll health endpoint until server wakes up or timeout
 * @param {number} maxAttempts - Maximum number of polling attempts (default: 15 = 45s)
 * @param {number} intervalMs - Interval between polls in milliseconds (default: 3000 = 3s)
 * @returns {Promise<boolean>} - True if server woke up, false if timeout
 */
const pollHealthEndpoint = async (maxAttempts = 15, intervalMs = 3000) => {
  for (let i = 0; i < maxAttempts; i++) {
    const isHealthy = await checkServerHealth();
    if (isHealthy) {
      return true;
    }
    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }
  return false;
};

/**
 * Wake up server if needed and show toast notification
 * @param {Function} showToastFn - Function to show toast notification (message, type)
 * @returns {Promise<boolean>} - True if server is ready, false if failed
 */
export const wakeUpServerIfNeeded = async (showToastFn) => {
  // Skip wake-up logic in production
  if (!isStaging) {
    return true;
  }

  // Check if server is already awake
  const isAwake = await checkServerHealth();
  if (isAwake) {
    return true;
  }

  // Server is sleeping - show toast and start polling
  showToastFn('Waking up server (~10–30s)...', 'info');

  const serverWokeUp = await pollHealthEndpoint();
  
  if (serverWokeUp) {
    showToastFn('Server ready! Retrying your request...', 'success');
    // Give a brief moment before retry
    await new Promise(resolve => setTimeout(resolve, 1000));
    return true;
  } else {
    showToastFn('Server unavailable. Please try again soon.', 'error');
    return false;
  }
};

/**
 * Wrapper for API calls with automatic wake-up retry
 * @param {Function} apiCallFn - The API call function to execute
 * @param {Function} showToastFn - Function to show toast notification
 * @returns {Promise<any>} - Result of the API call
 */
export const withServerWakeup = async (apiCallFn, showToastFn) => {
  try {
    // First attempt
    return await apiCallFn();
  } catch (error) {
    // Check if it's a connection/timeout error
    if (!error.response && isStaging) {
      // Likely server is sleeping - try to wake it up
      const serverReady = await wakeUpServerIfNeeded(showToastFn);
      
      if (serverReady) {
        // Retry the original request
        return await apiCallFn();
      } else {
        throw new Error('Server unavailable after wake-up attempt');
      }
    }
    // Re-throw other errors
    throw error;
  }
};

export default { wakeUpServerIfNeeded, withServerWakeup };
